import React from 'react';

import { AgentSessionListItemView } from '@confluence/entity-view/entry-points/agent-sessions/AgentSessionListItemView.ts';

import type { HomeRecentAgentSessionsQuery$data } from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import type { AgentSessionAssociation } from './useAgentSessionResumeProps.ts';
import { useAgentSessionResumeProps } from './useAgentSessionResumeProps.ts';

type AgentSessionNode = NonNullable<
	NonNullable<NonNullable<HomeRecentAgentSessionsQuery$data['agent_sessionQuery']>['nodes']>[number]
>;

type ConfluencePageEntity = Extract<
	NonNullable<NonNullable<AgentSessionNode['associatedEntities']>[number]>,
	{ __typename: 'ConfluencePage' }
>;

const getContentType = (type: ConfluencePageEntity['type'] | undefined): string | undefined => {
	switch (type) {
		case 'PAGE':
			return 'page';
		case 'BLOG_POST':
			return 'blogpost';
		case 'WHITEBOARD':
			return 'whiteboard';
		case 'DATABASE':
			return 'database';
		case 'EMBED':
			return 'embed';
		default:
			return undefined;
	}
};

const getAssociatedPage = (session: AgentSessionNode): ConfluencePageEntity | undefined => {
	const associatedPages =
		session.associatedEntities?.filter(
			(entity): entity is ConfluencePageEntity => entity?.__typename === 'ConfluencePage',
		) ?? [];

	return associatedPages.length === 1 ? associatedPages[0] : undefined;
};

export const AgentSessionListItem = ({
	session,
	associations,
}: {
	session: AgentSessionNode;
	associations?: AgentSessionAssociation[];
}): React.JSX.Element => {
	const associatedPage = getAssociatedPage(session);
	const contentType = getContentType(associatedPage?.type);
	const resumeProps = useAgentSessionResumeProps(session, associations);

	return (
		<AgentSessionListItemView
			{...resumeProps}
			title={associatedPage?.title || session.conversationChannel?.name}
			agentName={session.agent?.name}
			agentPicture={session.agent?.picture}
			contentType={contentType}
			contentSubType={associatedPage?.subtype === 'LIVE' ? 'live' : undefined}
			space={
				associatedPage?.space
					? {
							name: associatedPage.space.name,
							spaceKey: associatedPage.space.key,
						}
					: undefined
			}
		/>
	);
};
